import { useUserStore } from '@/stores/user';
import { useProgramStore } from '@/stores/programs';
import { useSiteStore } from '@/stores/site';
import {
    isProgramSiteContextError,
    resolveAuthorizedProgramSiteId,
} from '@/components/program/programSiteContext';

const enterProgramRoute = (to, _from, next) => {
    const userStore = useUserStore();
    userStore.sync(async () => {
        if (!userStore.canUsePrograms()) {
            next('/');
            return;
        }

        const siteStore = useSiteStore();
        siteStore.normalizeGlobalSiteAuthorization();
        const siteId = resolveAuthorizedProgramSiteId(
            siteStore.globalSiteId,
            siteStore.userSiteIds
        );
        const programStore = useProgramStore();
        programStore.setSiteContext(
            siteId,
            userStore.company?.id || userStore.user?.company_id || null
        );

        if (!siteId) {
            if (siteStore.globalSiteId != null) siteStore.setGlobalSiteId(null);
            next();
            return;
        }

        try {
            await programStore.ensureLoaded(siteId);
            if (to.params.hash) {
                await programStore.ensureProgram(to.params.hash, siteId);
            }
        } catch (error) {
            if (isProgramSiteContextError(error)) {
                siteStore.setGlobalSiteId(null);
            }
            // Program views render catalog transport errors with retry controls.
        }
        next();
    });
};

export default [
    {
        path: '/',
        name: 'Home',
        component: () => import('../views/Home.vue'),
        meta: {
            header: 'Dashboard',
            headerKey: 'navigation.dashboard',
            hideMobileHeader: true,
        },
    },
    {
        path: '/about',
        name: 'About',
        component: () => import('../views/About.vue'),
    },
    {
        path: '/test',
        name: 'Test',
        component: () => import('../views/TestView.vue'),
    },
    {
        path: '/test2',
        name: 'Test2',
        component: () => import('../views/TestView2.vue'),
    },
    {
        path: '/test3',
        name: 'Test3',
        component: () => import('../views/TestView3.vue'),
    },
    {
        path: '/register',
        name: 'Register',
        component: () => import('../views/RegistrationFacade.vue'),
        meta: {
            requiresAuth: false,
        },
    },
    {
        path: '/login',
        name: 'Login',
        component: () => import('../views/Login.vue'),
        meta: {
            requiresAuth: false,
        },
    },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import('../views/ForgotPassword.vue'),
        meta: {
            requiresAuth: false,
        },
    },
    {
        path: '/reset-password/:token',
        name: 'ResetPassword',
        component: () => import('../views/ResetPassword.vue'),
        meta: {
            requiresAuth: false,
        },
    },
    {
        path: '/finalize-account/:token',
        name: 'FinalizeAccount',
        component: () => import('../views/ResetPassword.vue'),
        meta: {
            requiresAuth: false,
        },
    },
    {
        path: '/wordpress-connection/complete',
        name: 'WordPress Connection Complete',
        component: () => import('../views/WordPressConnectionComplete.vue'),
        meta: {
            requiresAuth: false,
        },
    },
    {
        path: '/team',
        name: 'Team',
        component: () => import('../views/Team.vue'),
        meta: {
            header: 'Team',
            headerKey: 'navigation.team',
        },
    },
    {
        path: '/team/:hash',
        name: 'Employee Detail',
        component: () => import('../views/EmployeeDetail.vue'),
        meta: {
            header: 'Employee Profile',
            headerKey: 'navigation.employeeProfile',
            employeeDetail: true,
        },
    },
    {
        path: '/inspections',
        name: 'Inspections',
        component: () => import('../views/Inspections.vue'),
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (
                    !userStore?.hasPermOneOf(
                        'inspection:read',
                        'inspection:write'
                    )
                ) {
                    next('/');
                } else {
                    next();
                }
            });
        },
        meta: {
            header: 'Inspections',
            headerKey: 'navigation.inspections',
        },
    },
    {
        path: '/surveillance/:hash?',
        name: 'Surveillance',
        component: () => import('../views/Surveillance.vue'),
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (!userStore.programEligibleSectorAlphaAccess()) {
                    next('/');
                } else {
                    next();
                }
            });
        },
        meta: {
            header: 'Surveillance',
            headerKey: 'navigation.surveillance',
        },
    },
    {
        path: '/outbreak',
        name: 'Outbreak',
        component: () => import('../views/Outbreak.vue'),
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (!userStore.programEligibleSectorAlphaAccess()) {
                    next('/');
                } else {
                    next();
                }
            });
        },
        meta: {
            header: 'Outbreak',
            headerKey: 'navigation.outbreak',
        },
    },
    {
        path: '/audits/:hash/overview',
        name: 'AuditOverview',
        component: () => import('../views/AuditOverview.vue'),
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (!userStore.hasPermOneOf('audits:read', 'audits:write')) {
                    next({ name: 'Home', replace: true });
                    return;
                }
                const crossCompanyBypass =
                    userStore.isAppAdmin ||
                    Boolean(userStore.user?.is_app_consultant);
                if (
                    !crossCompanyBypass &&
                    !userStore.companyHasPerm('audits:read')
                ) {
                    next({ name: 'Home', replace: true });
                    return;
                }
                next();
            });
        },
        meta: {
            header: 'Assessment Overview',
            headerKey: 'navigation.assessmentOverview',
        },
    },
    {
        path: '/audits/:hash?',
        name: 'Audits',
        component: () => import('../views/Audits.vue'),
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (!userStore.hasPermOneOf('audits:read', 'audits:write')) {
                    next({ name: 'Home', replace: true });
                    return;
                }

                const crossCompanyBypass =
                    userStore.isAppAdmin ||
                    Boolean(userStore.user?.is_app_consultant);
                if (
                    !crossCompanyBypass &&
                    !userStore.companyHasPerm('audits:read')
                ) {
                    next({ name: 'Home', replace: true });
                    return;
                }

                next();
            });
        },
        meta: {
            header: 'Auditing Suite',
            headerKey: 'navigation.auditingSuite',
        },
    },
    {
        path: '/analysis',
        name: 'Analysis',
        component: () => import('../views/Analysis.vue'),
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (!userStore.programEligibleSectorAlphaAccess()) {
                    next('/');
                } else {
                    next();
                }
            });
        },
        meta: {
            header: 'Analysis',
            headerKey: 'navigation.analysis',
        },
    },
    {
        path: '/library/:hash?',
        name: 'Library',
        component: () => import('../views/Library.vue'),
        meta: {
            header: 'Knowledge Library',
            headerKey: 'navigation.knowledgeLibrary',
        },
    },
    {
        path: '/program',
        name: 'Program',
        component: () => import('../views/Program.vue'),
        beforeEnter: enterProgramRoute,
        meta: {
            header: 'Program',
            headerKey: 'navigation.program',
        },
    },
    {
        path: '/program/:hash',
        name: 'Program Detail',
        component: () => import('../views/ProgramDetail.vue'),
        beforeEnter: enterProgramRoute,
        meta: {
            header: 'Program',
            headerKey: 'navigation.programOverview',
        },
    },
    {
        // Deprecated fallback: keeps the legacy full Program page (and its
        // in-page URL sync for courses/action points/library) reachable
        // for existing deep links while /program/:hash serves the new
        // slim ProgramDetail page.
        path: '/program1/:hash',
        name: 'Program Overview',
        component: () => import('../views/ProgramOverview.vue'),
        beforeEnter: enterProgramRoute,
        meta: {
            header: 'Program Overview',
            headerKey: 'navigation.programOverview',
        },
    },
    {
        path: '/secondment',
        name: 'Secondment',
        component: () => import('../views/Secondments.vue'),
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (!userStore?.hasPermOneOf('secondments:read')) {
                    next('/');
                } else {
                    next();
                }
            });
        },
        meta: {
            header: 'Secondment',
            headerKey: 'navigation.secondment',
        },
    },
    {
        path: '/inventory/:hash?',
        name: 'Inventory',
        component: () => import('../views/Inventory.vue'),
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (!userStore?.hasPermOneOf('inventory:read')) {
                    //if user is not a company admin - redirect to home;
                    // TODO: forward to dont have access page
                    next('/');
                } else {
                    next();
                }
            });
        },
        meta: {
            header: 'Inventory Management',
            headerKey: 'navigation.inventoryManagement',
        },
    },
    {
        path: '/courses/:hash?',
        name: 'Courses',
        component: () => import('../views/Courses.vue'),
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (!userStore?.hasPermOneOf('courses:read', 'courses:write')) {
                    //if user is not a company admin - redirect to home;
                    // TODO: forward to dont have access page
                    next('/');
                } else {
                    next();
                }
            });
        },
        meta: {
            header: 'Education Tracking',
            headerKey: 'navigation.educationTracking',
        },
    },
    {
        path: '/settings',
        name: 'Settings',
        component: () => import('../views/Settings.vue'),
        meta: {
            header: 'Settings',
            headerKey: 'navigation.settings',
        },
    },
    {
        path: '/contact-us',
        name: 'Contact Us',
        component: () => import('../views/ContactUs.vue'),
        meta: {
            header: 'Contact Us',
            headerKey: 'navigation.contactUs',
        },
    },
    {
        path: '/help',
        name: 'Help',
        component: () => import('../views/Help.vue'),
        meta: {
            header: 'Help',
            headerKey: 'navigation.help',
        },
    },
    {
        path: '/release-notes',
        name: 'ReleaseNotes',
        component: () => import('../views/ReleaseNotes.vue'),
        meta: {
            header: 'Release Notes',
            headerKey: 'navigation.releaseNotes',
        },
    },
    {
        path: '/admin',
        name: 'Admin',
        meta: {
            header: 'Admin Panel',
            headerKey: 'navigation.adminPanel',
        },
        beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            userStore.sync(() => {
                if (!userStore.isAppAdmin) {
                    next('/');
                } else {
                    next();
                }
            });
        },
        /*
         * ADMIN COMPANY CONTEXT (employees / ajax company_id override / sites)
         *
         * Every child route here that represents "view this admin company / its data"
         * MUST set meta.adminCompanyContext: true so `router/index.js` can sync
         * Pinia admin workflow state on enter/leave and when the company hash changes.
         *
         * URL → company hash (used by syncAdminCompanyWorkflowContext):
         * - `company/:hash` → param name `hash` (picked up by default resolution).
         * - `company/:companyHash/...` → param `companyHash`.
         *
         * If you add a route with another param name, set BOTH:
         *   meta: { adminCompanyContext: true, adminCompanyHashParam: 'yourParam' }
         * and update conventions in `router/index.js` docs if helpful.
         */
        children: [
            {
                path: '',
                component: () => import('../views/Admin.vue'),
                name: 'AdminPanel',
            },
            {
                path: 'programs/:programCode',
                name: 'ProgramResourcesAdmin',
                component: () =>
                    import('../views/admin/program/ProgramResourcesAdmin.vue'),
                meta: {
                    header: 'Program Resources',
                },
            },
            {
                path: 'company/:hash',
                name: 'Company Detail',
                component: () => import('../views/admin/general/Company.vue'),
                meta: {
                    adminCompanyContext: true,
                },
            },
            {
                path: 'company/:companyHash/employees/:employeeHash',
                name: 'AdminEmployeeDetail',
                component: () => import('../views/EmployeeDetail.vue'),
                meta: {
                    header: 'Employee Details',
                    adminCompanyContext: true,
                    employeeDetail: true,
                },
            },
            {
                path: 'company/:companyHash/audits/:auditHash/overview',
                name: 'AdminAuditOverview',
                component: () => import('../views/AuditOverview.vue'),
                meta: {
                    header: 'Assessment Overview',
                    headerKey: 'navigation.assessmentOverview',
                    adminCompanyContext: true,
                },
            },
        ],
    },
    {
        path: '/pdf-viewer/:hash',
        name: 'PdfViewer',
        component: () => import('../views/PdfViewer.vue'),
    },

    // Keep this route as the last one
    {
        path: '/:pathMatch(.*)*',
        name: 'PageNotFound',
        component: () => import('../views/PageNotFound.vue'),
        meta: {
            requiresAuth: false,
        },
    },
];
